import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { SerializedAccountForList } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../common/types';
import { UserAvatar } from '../common/views/userAvatar/UserAvatar';
/* eslint-disable css-modules/no-unused-class -- shared with ArtistSwitcher.tsx; each file uses its own slice of it */
import styles from './ArtistSwitcher.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistSwitcherPillProps {
  variant: 'sidebar' | 'topBar' | 'compact';
  active: SerializedAccountForList | undefined;
  accountCount: number;
  /** `null` means unlimited. */
  artistsLimit: number | null;
  /** The active workspace name. Set only when the user has two or more workspaces. */
  workspaceName: string | null;
}

/** The content of the switcher pill: the avatar, the active artist and, in the sidebar, one line under it. */
const ArtistSwitcherPillComponent: React.FC<ArtistSwitcherPillProps> = ({
  variant,
  active,
  accountCount,
  artistsLimit,
  workspaceName,
}) => {
  const intl = useIntl();
  const isCompact = variant === 'compact';
  const isSidebar = variant === 'sidebar';
  return (
    <>
      <UserAvatar size={isCompact ? 24 : 26} url={active?.avatarProcessedUrl} name={active?.name} />
      <span className={styles.meta}>
        {/* A workspace with no artist shows the workspace name in place of the artist. */}
        <span className={styles.name}>{active?.name ?? workspaceName ?? ''}</span>
        {/* design: workspaces a1. With two or more workspaces, the active workspace name. */}
        {isSidebar && active !== undefined && workspaceName !== null && (
          <small className={styles.workspaceLine}>{workspaceName}</small>
        )}
        {/* design s1: the switcher count line, under the active artist's name. */}
        {isSidebar && workspaceName === null && artistsLimit !== null && (
          <small className={styles.count}>
            {intl.formatMessage(
              { id: 'app.sml.artistSwitcher.buttonCount', defaultMessage: '{count} / {limit} artists' },
              { count: accountCount, limit: artistsLimit }
            )}
          </small>
        )}
      </span>
      {isCompact && <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />}
    </>
  );
};

export const ArtistSwitcherPill = TypedMemo(ArtistSwitcherPillComponent);
